import type { HTMLAttributes, ReactNode } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "../../lib/cn";
import { Button, type ButtonProps } from "../button";
import { Tooltip } from "../overlays";

export type MessageFrom = "user" | "assistant" | "system";

export function Message({ from, className, ...props }: HTMLAttributes<HTMLDivElement> & { from: MessageFrom }) {
  return <div data-from={from} className={cn("group/message flex w-full min-w-0", from === "user" ? "justify-end" : "justify-start", className)} {...props} />;
}

/** The user's words sit in a soft bubble at the end of the line; the assistant's answer is plain text across the column. */
export function MessageContent({ from, className, ...props }: HTMLAttributes<HTMLDivElement> & { from: MessageFrom }) {
  return (
    <div
      className={cn(
        "min-w-0 text-sm",
        from === "user" ? "max-w-[85%] rounded-2xl rounded-ee-md bg-sand-100 px-4 py-2.5 leading-relaxed text-aeblack-900" : "w-full text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Who is answering: the assistant's mark and name (a specialist's, when one replied), with optional detail after it. */
export function MessageHeader({ name, icon, meta, className }: { name: ReactNode; icon?: ReactNode; meta?: ReactNode; className?: string }) {
  return (
    <div className={cn("mb-1.5 flex min-w-0 items-center gap-2", className)}>
      <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-aegold-100 to-seablue-100 text-aegold-800 ring-1 ring-aegold-200/70 [&_svg]:size-3.5">{icon ?? <Sparkles />}</span>
      <span className="truncate text-xs font-semibold text-aeblack-900">{name}</span>
      {meta ? <span className="truncate text-[11px] text-muted-foreground">{meta}</span> : null}
    </div>
  );
}

export function MessageActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mt-2 flex flex-wrap items-center gap-0.5 text-[11px] text-muted-foreground", className)} {...props} />;
}

export function MessageAction({ label, tooltip, className, children, ...props }: ButtonProps & { label: string; tooltip?: string }) {
  return (
    <Tooltip content={tooltip ?? label}>
      <Button type="button" size="icon-sm" variant="ghost" aria-label={label} className={cn("size-7 rounded-md text-sand-700 hover:text-aeblack-900 [&_svg]:size-3.5", className)} {...props}>
        {children}
      </Button>
    </Tooltip>
  );
}
